// Stimmen-Abschnitte („Runs") eines Takts: ein Takt wird an seinen `<backup>`s in die Stücke zerlegt,
// die jeweils am Taktanfang beginnen (eine Stimme bzw. ein Staff). Gemeinsame Basis für Eingriffe,
// die einen Takt vorne oder hinten kürzen bzw. auffüllen – den Auftakt (`pickup.ts`) und das
// Ein-Takt-Metrum (`meterChange.ts`). Abhängigkeitsregel wie im ganzen Ordner: nur `core.ts`.
import {ticksToRestElements, voiceOf} from './core';

export const tagOf = (el: Element) => el.tagName.toLowerCase();
export const durationOf = (el: Element) => parseInt(el.querySelector('duration')?.textContent || '0', 10) || 0;

/** Zeitdauer, die ein Kind-Element des Takts in seiner Stimme verbraucht (Primärnote, Pause, `<forward>`). */
export function timedTicks(el: Element): number {
    const tag = tagOf(el);
    if (tag === 'forward') return durationOf(el);
    if (tag === 'note' && !el.querySelector('chord') && !el.querySelector('grace')) return durationOf(el);
    return 0;
}

export function isRestLike(el: Element): boolean {
    return tagOf(el) === 'forward' || (tagOf(el) === 'note' && !!el.querySelector('rest') && !el.querySelector('grace'));
}

/** Ein Abschnitt des Takts zwischen zwei `<backup>`: eine Stimme (bzw. Staff), ab Taktanfang. */
export interface Run {
    elements: Element[];
    backupAfter: Element | null;
    length: number;
}

/** Zerlegt den Takt an seinen `<backup>`s. `null`, wenn ein `<backup>` nicht exakt an den Taktanfang
 *  zurückspringt – dann beginnt eine Stimme mitten im Takt, und ein Kürzen am Anfang verschöbe sie. */
export function splitRuns(measure: Element): Run[] | null {
    const runs: Run[] = [];
    let elements: Element[] = [];
    let cursor = 0;
    for (const child of [...measure.children]) {
        if (tagOf(child) === 'backup') {
            if (durationOf(child) !== cursor) return null;
            runs.push({elements, backupAfter: child, length: cursor});
            elements = [];
            cursor = 0;
            continue;
        }
        elements.push(child);
        cursor += timedTicks(child);
    }
    runs.push({elements, backupAfter: null, length: cursor});
    return runs.filter(r => r.elements.some(el => timedTicks(el) > 0));
}

export function leadingRestTicks(run: Run): number {
    let ticks = 0;
    for (const el of run.elements) {
        if (tagOf(el) === 'note' && el.querySelector('grace')) break;
        const t = timedTicks(el);
        if (!t) continue;
        if (!isRestLike(el)) break;
        ticks += t;
    }
    return ticks;
}

export function trailingRestTicks(run: Run): number {
    let ticks = 0;
    for (const el of [...run.elements].reverse()) {
        const tag = tagOf(el);
        if (tag !== 'note' && tag !== 'forward') continue;
        if (!isRestLike(el)) break;   // klingende Note, Akkord-Begleiter oder Vorschlag
        ticks += timedTicks(el);
    }
    return ticks;
}

/** Ersetzt eine Pause/ein `<forward>` durch eine kürzere Fassung mit `ticks`. */
export function shortenRest(doc: Document, el: Element, ticks: number, divisions: number): void {
    if (tagOf(el) === 'forward') {
        el.querySelector('duration')!.textContent = String(ticks);
        return;
    }
    const staff = el.querySelector('staff')?.textContent ?? null;
    el.replaceWith(...ticksToRestElements(doc, ticks, divisions, voiceOf(el), staff));
}

/** Entfernt `ticks` Pausenzeit vom Anfang (`fromEnd = false`) bzw. Ende der Stimme. Voraussetzung:
 *  so viel Pausenzeit liegt dort (siehe {@link leadingRestTicks}/{@link trailingRestTicks}). */
export function trimRun(doc: Document, run: Run, ticks: number, divisions: number, fromEnd: boolean): void {
    let remaining = ticks;
    const order = fromEnd ? [...run.elements].reverse() : run.elements;
    for (const el of order) {
        if (remaining <= 0) break;
        const t = timedTicks(el);
        if (!t || !isRestLike(el)) continue;
        if (t <= remaining) {
            el.remove();
            remaining -= t;
        } else {
            shortenRest(doc, el, t - remaining, divisions);
            remaining = 0;
        }
    }
    adjustBackup(run, -ticks);
}

/** Fügt `ticks` Pausenzeit am Anfang bzw. Ende der Stimme ein (Stimme/Staff der ersten Note). */
export function padRun(doc: Document, run: Run, ticks: number, divisions: number, atEnd: boolean): void {
    const timed = run.elements.filter(el => timedTicks(el) > 0);
    const anchor = atEnd ? timed[timed.length - 1] : timed[0];
    const sample = run.elements.find(el => tagOf(el) === 'note');
    if (!anchor || !sample) return;
    const rests = ticksToRestElements(doc, ticks, divisions, voiceOf(sample), sample.querySelector('staff')?.textContent ?? null);
    if (atEnd) {
        // Akkord-Begleiter hängen hinter ihrer Primärnote – die Pausen erst danach einfügen.
        let after: Element = anchor;
        while (after.nextElementSibling && tagOf(after.nextElementSibling) === 'note'
            && after.nextElementSibling.querySelector('chord')) {
            after = after.nextElementSibling;
        }
        after.after(...rests);
    } else {
        // Ein Akkordsymbol direkt vor der ersten Note gehört zu ihr – die Pausen davor einfügen,
        // sonst rutschte es an den neuen Taktanfang.
        let before: Element = anchor;
        while (before.previousElementSibling && tagOf(before.previousElementSibling) === 'harmony') {
            before = before.previousElementSibling;
        }
        before.before(...rests);
    }
    adjustBackup(run, ticks);
}

/** Zieht das `<backup>` hinter dem Abschnitt auf dessen neue Länge (`length + delta`) nach. */
export function adjustBackup(run: Run, delta: number): void {
    const dur = run.backupAfter?.querySelector('duration');
    if (dur) dur.textContent = String(run.length + delta);
}
